import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormattedMessage, injectIntl } from "react-intl";
import { useMutation, gql } from "@apollo/client";
import { useDispatch } from "react-redux";
import { authSlice } from "../../Store/modules/AuthModule/authSlice";
import { adminLoginAction, FetchOrgDetails } from "../../apis";
import { Snackbar } from '@material-ui/core'
import MuiAlert from "@material-ui/lab/Alert";
import { Link } from "react-router-dom";

import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';



const { actions } = authSlice;

const initialValues = {
  email: "",
  password: "",
};

const LOGIN_USER = gql`
  mutation Mutation($adminLoginEmail: String, $adminLoginPassword: String) {
    adminLogin(email: $adminLoginEmail, password: $adminLoginPassword) {
      code
      success
      message
      accessToken
      user {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function Login(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const [login] = useMutation(LOGIN_USER);
  const dispatch = useDispatch();

  const [snackBarText, setSnackBarText] = React.useState("");
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);
  const [snackBarVariant, setSnackBarVariant] = React.useState("success");

  const [showPassword, setShowPassword] = React.useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email("Wrong email format")
      .min(3, "Minimum 3 symbols")
      .max(50, "Maximum 50 symbols")
      .required(
        intl.formatMessage({
          id: "AUTH.VALIDATION.REQUIRED_FIELD",
        })
      ),
    password: Yup.string()
      .min(3, "Minimum 3 symbols")
      .max(50, "Maximum 50 symbols")
      .required(
        intl.formatMessage({
          id: "AUTH.VALIDATION.REQUIRED_FIELD",
        })
      ),
  });

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }

    return "";
  };

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      enableLoading();

      try{
        let res = await adminLoginAction({ email: values.email, password: values.password })

        console.log(res);
        if (res.success) {

          dispatch(actions.setUserDetails({ user: res.user }));
          dispatch(actions.login({ accessToken: res.accessToken }));

          if (res.user?.organization) {
            try {
              const org = await FetchOrgDetails(res.user.organization);
              const logoUrl = org?.organization?.logo || org?.logo;
              if (logoUrl) {
                dispatch(actions.updateOrgLogo({ orgId: res.user.organization, logoUrl }));
              }
            } catch (_) {}
          }
        } 
      }catch(ex){
        if(ex.response){
            let data = ex.response.data;
            if(data.url){
              setSnackBarVariant("error");
              setSnackBarText("Licence not active");
              setSnackBarOpen(true);
              window.location.href = data.url
            }else{
              setSnackBarVariant("error");
              setSnackBarText(data.message);
              setSnackBarOpen(true);
              disableLoading();
            }
        }else{
          setSnackBarVariant("error");
          setSnackBarText(ex.message);
          setSnackBarOpen(true);
          disableLoading();
        }
      }
      // disableLoading();

      // console.log(err);
      // disableLoading();

    },
  });

  return (
    <div className="login-form login-signin" id="kt_login_signin_form">
      {/* begin::Head */}
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">
          <FormattedMessage id="AUTH.LOGIN.TITLE" />
        </h3>
        <p className="text-muted font-weight-bold">
          Enter your username and password
        </p>
      </div>
      {/* end::Head */}

      {/*begin::Form*/}
      <form
        onSubmit={formik.handleSubmit}
        className="form fv-plugins-bootstrap fv-plugins-framework"
      >
        {formik.status ? (
          <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
            <div className="alert-text font-weight-bold">{formik.status}</div>
          </div>
        ) : null}

        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder="Email"
            type="email"
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
              "email"
            )}`}
            name="email"
            {...formik.getFieldProps("email")}
          />
          {formik.touched.email && formik.errors.email ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.email}</div>
            </div>
          ) : null}
        </div>
        <div className="form-group fv-plugins-icon-container position-relative">
          <input
            placeholder="Password"
            type={showPassword ? "text" : "password"}
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
              "password"
            )}`}
            name="password"
            {...formik.getFieldProps("password")}
          />
          <span onClick={toggleShowPassword} style={{ cursor: "pointer", position: "absolute", right: "10px", top: "15px" }}>
            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </span>
          {formik.touched.password && formik.errors.password ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.password}</div>
            </div>
          ) : null}
        </div>
        <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
          <Link
            to="/auth/forgot-password"
            className="text-dark-50 text-hover-primary my-3 mr-2"
            id="kt_login_forgot"
          >
            <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
          </Link>
          <button
            id="kt_login_signin_submit"
            type="submit"
            disabled={formik.isSubmitting}
            className={`btn btn-primary font-weight-bold px-9 py-4 my-3`}
          >
            <span>Sign In</span>
            {loading && <span className="ml-3 spinner spinner-white"></span>}
          </button>
        </div>
      </form>
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={5000}
        onClose={()=>{setSnackBarOpen(false)}}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={()=>{setSnackBarOpen(false)}}
          style={{
            fontSize: "16px",
            backgroundColor: snackBarVariant === "error" ? "red" : "",
          }}
          severity={snackBarVariant}
        >
          {snackBarText}
        </Alert>
      </Snackbar>
      {/*end::Form*/}
    </div>
  );
}

export default injectIntl(Login);
